// Hideo - the app's page and start-up state: the page shown (`app.page`), whether the app has started (`app.booted`),
// page switching (showPage / switchTab), the full redraw (renderAll) and the start with the active profile (boot).
// Ported from the legacy core/app.js (verbatim copy in tests/fixtures/legacy/app.js, golden tests in tests/boot.test.ts).
// A plain object, not a Pinia store, on purpose: like the legacy `curTab` / `appBooted` it is not reactive (nothing
// redraws when it changes: menus and the create button follow the route). Callers use `app.x` at call time, so tests can
// spy on it (vi.spyOn(app, 'switchTab')) and set its state.
import { isPage, type PageId } from '../navigation'
import { navigate } from '../router'
import { useUiStore } from '../stores/ui'
import { usePedStore } from '../stores/ped'
import { useSourcesStore } from '../stores/sources'
import { useDataStore } from '../stores/data'
import { useProjectsStore } from '../stores/projects'
import { usePickerStore } from '../stores/picker'
import { useSettingsStore } from '../stores/settings'
import { initFolder } from './storage/folder'
import { mediaMigrateAll } from './cloud/media'

export const app = {
  /** The page shown now */
  page: 'home' as PageId,
  /** The app has started with a profile (boot ran) */
  booted: false,
  /** Shows a page at once (an unknown id shows Home): closes the phone menu and remembers the page. Pages are Vue
   *  components drawn by the router (src/router.ts); on Back/Forward or a typed address the router calls this */
  showPage(page: string): void {
    const id: PageId = isPage(page) ? page : 'home'
    useUiStore().closeMobileMenu()
    app.page = id
  },
  /** Shows a page and updates the address (replace: without a new history entry) */
  switchTab(page: string, replace?: boolean): void {
    app.showPage(page)
    navigate(app.page, !!replace)
  },
  /** Full redraw: a format filter whose pill is gone goes back to "All", the Vue pages redraw (ui.tick), the Sources page
   *  keeps its open tile and the starting tag of a new source */
  renderAll(): void {
    useDataStore().invalidate()   // a full redraw sees the data normalised again, as when every read normalised it
    usePedStore().sync()
    useUiStore().refresh()
    useSourcesStore().sync(useDataStore().getData(), useProjectsStore().active())
  },
  /** Starts the app with the active profile, once: redraw, the profile at the top, the legacy data folder, the start
   *  page, then the Base64 images and voice notes of old saves moved out of the browser storage. Without a profile (a new
   *  installation) it waits for the first one */
  boot(): void {
    if (app.booted || !useDataStore().currentProfile()) return
    app.booted = true
    app.renderAll()
    usePickerStore().renderUi()
    void initFolder()
    app.switchTab(useSettingsStore().values.startTab, true)
    void mediaMigrateAll()
  }
}
